import React, { useState, useRef, useEffect } from 'react';
import BaseModal from '../common/BaseModal';
import api from '../../services/api';

const CouponCheckModal = ({ show, onClose }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Ustaw focus na input po otwarciu modalu
  useEffect(() => {
    if (show && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const handleCheck = async () => {
    if (!code || code.length < 4) {
      setError('Kod kuponu musi miec minimum 4 znaki');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.get(`/coupons/validate/${code.toUpperCase()}`);
      const data = response.data;

      if (data.success && data.data) {
        setResult(data.data);
      } else {
        setError(data.message || 'Nie znaleziono kuponu');
      }
    } catch (err) {
      // Axios rzuca błąd dla kodów 4xx/5xx - pobierz wiadomość z odpowiedzi
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Blad polaczenia z serwerem');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setResult(null);
    setError(null);
    onClose();
  };

  const footer = (
    <button
      onClick={handleClose}
      style={{
        width: "100%",
        padding: "6px 12px",
        fontSize: "12px",
        border: "2px solid var(--text-muted)",
        backgroundColor: "var(--card-bg)",
        color: "var(--text-muted)",
        cursor: "pointer",
        fontWeight: "500",
      }}
    >
      Zamknij
    </button>
  );

  return (
    <BaseModal
      isOpen={show}
      onClose={handleClose}
      title="Sprawdz kupon"
      headerStyle={{ backgroundColor: "var(--purple)" }}
      size="sm"
      footer={footer}
    >
      <div style={{ marginBottom: "1rem" }}>
        <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "500", fontSize: "0.875rem" }}>
          Kod kuponu:
        </label>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <input
            ref={inputRef}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Wprowadz kod"
            style={{
              flex: 1,
              padding: "0.5rem 0.75rem",
              border: "2px solid var(--border)",
              borderRadius: "6px",
              fontSize: "1rem",
              textAlign: "center",
              letterSpacing: "2px",
              fontWeight: "600",
            }}
            disabled={loading}
            onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
          />
          <button
            onClick={handleCheck}
            disabled={loading || code.length < 4}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              backgroundColor: loading || code.length < 4 ? "var(--text-muted)" : "var(--purple)",
              color: "#fff" ,
              cursor: loading || code.length < 4 ? "not-allowed" : "pointer",
              fontWeight: "500",
            }}
          >
            {loading ? "..." : "Sprawdz"}
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "0.75rem",
            backgroundColor: "var(--danger-light)",
            color: "var(--danger)",
            borderRadius: "6px",
            marginBottom: "1rem",
            fontSize: "0.875rem",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div
          style={{
            padding: "1rem",
            backgroundColor: result.valid ? "var(--success-light)" : "var(--warning-light)",
            border: `1px solid ${result.valid ? "var(--success-light)" : "var(--warning-light)"}`,
            borderRadius: "8px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <span style={{ fontWeight: "500", color: "var(--text-secondary)" }}>Status:</span>
            <span style={{
              fontWeight: "600",
              color: result.valid ? "var(--success)" : "var(--warning)"
            }}>
              {result.valid ? "Aktywny" : "Nieaktywny"}
            </span>
          </div>

          {result.value !== undefined && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <span style={{ fontWeight: "500", color: "var(--text-secondary)" }}>Saldo:</span>
              <span style={{ fontWeight: "700", fontSize: "1.25rem", color: "var(--success)" }}>
                {parseFloat(result.value).toFixed(2)} zl
              </span>
            </div>
          )}

          {result.initial_value !== undefined && result.initial_value !== result.value && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <span style={{ fontWeight: "500", color: "var(--text-secondary)" }}>Wartosc poczatkowa:</span>
              <span style={{ color: "var(--text-muted)" }}>
                {parseFloat(result.initial_value).toFixed(2)} zl
              </span>
            </div>
          )}

          {result.expiry_date && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <span style={{ fontWeight: "500", color: "var(--text-secondary)" }}>Wazny do:</span>
              <span style={{ color: "var(--text-secondary)" }}>
                {new Date(result.expiry_date).toLocaleDateString('pl-PL')}
              </span>
            </div>
          )}

          {result.customer_name && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: "500", color: "var(--text-secondary)" }}>Klient:</span>
              <span style={{ color: "var(--text-secondary)" }}>
                {result.customer_name}
              </span>
            </div>
          )}

          {result.errors && result.errors.length > 0 && (
            <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "var(--warning)" }}>
              {result.errors.join(', ')}
            </div>
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default React.memo(CouponCheckModal);
